/**
 * Shifts Edit Page — hardcoded form.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne, useUpdate } from '@refinedev/core';
import {
  Button, Card, CardContent, Input, Label, Checkbox,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

export function ShiftsEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading: fetchLoading } = useOne({ resource: 'shifts', id: id! });
  const { mutate: update, isLoading: updateLoading } = useUpdate();
  const [form, setForm] = React.useState<Record<string, unknown> | null>(null);

  React.useEffect(() => { if (data?.data) setForm({ ...data.data }); }, [data]);
  const set = (f: string, v: unknown) => setForm(p => p ? { ...p, [f]: v } : p);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form) return;
    const { id: _id, org_id, date_created, date_updated, ...values } = form;
    update({ resource: 'shifts', id: id!, values }, { onSuccess: () => navigate(`/shifts/${id}`) });
  }

  if (fetchLoading || !form) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="p-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/shifts/${id}`)}><ArrowLeft className="h-4 w-4" /></Button>
        <h1 className="text-2xl font-semibold">Edit Shift</h1>
      </div>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="name">Shift Name *</Label>
              <Input id="name" value={String(form.name ?? '')} onChange={e => set('name', e.target.value)} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="start_time">Start Time</Label>
                <Input id="start_time" type="time" value={String(form.start_time ?? '')} onChange={e => set('start_time', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="end_time">End Time</Label>
                <Input id="end_time" type="time" value={String(form.end_time ?? '')} onChange={e => set('end_time', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="break_duration">Break Duration (minutes)</Label>
              <Input id="break_duration" type="number" min={0} max={480} value={Number(form.break_duration ?? 60)} onChange={e => set('break_duration', parseInt(e.target.value) || 0)} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={Boolean(form.is_night_shift)} onCheckedChange={v => set('is_night_shift', v)} />
              <span className="text-sm">Night Shift</span>
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(`/shifts/${id}`)}>Cancel</Button>
              <Button type="submit" disabled={updateLoading}>{updateLoading ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default ShiftsEditPage;
